import React, { FC, memo, useCallback } from 'react';
import { GeolocationCoords } from 'app/types/common';
import { WarehouseForBooking, getWorkingScheduleString } from 'entities/Warehouse';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { ReactComponent as LocationIcon } from 'shared/assets/icons/newIcons/LocationIcon.svg';
import { ReactComponent as TimeIcon } from 'shared/assets/icons/newIcons/TimeIcon.svg';
import WarehouseDefaultImage from 'shared/assets/images/WarehouseDefaultImage.png';
// import { ModernSizeGroupsTags } from 'entities/SizeCode';
import { getBoxDiscountedPriceAmount, getBoxPriceAmountWithVat, useGetMinBoxPriceAmount } from 'entities/Box';
import { useGetCurrencySymbol, getGlobalSettings } from 'app/appState';
import { useAppSelector } from 'app/config/storeConfig/hooks';
import { getLoggedUserData } from 'entities/User';
import { formatPrice } from 'shared/utils/helpers/formatPrice';

interface ModernWarehouseCardMobileProps {
  warehouse: WarehouseForBooking;
  distance?: Nullable<number>;
  isOutlined: boolean;
  onSelectCard: () => void;
  onGetRoute: (coords: GeolocationCoords, warehouseId: string) => void;
  onClearRoute: () => void;
}

export const ModernWarehouseCardMobile: FC<ModernWarehouseCardMobileProps> = memo((props) => {
  const {
    warehouse: {
      name,
      imageUrl,
      address,
      zip,
      workingSchedule,
      // sizeCodes,
      availableBoxesAmount,
      warehouseDiscounts,
      minPrice,
      // longitude,
      // latitude,
    },
    isOutlined,
    distance,
    onSelectCard,
  } = props;

  const currencySymbol = useGetCurrencySymbol();
  const globalSettings = useAppSelector(getGlobalSettings);
  const loggedUser = useAppSelector(getLoggedUserData);
  const { t } = useAppTranslation(['booking', 'common']);

  const fullAddress = `${address} ${zip || ''}`;
  const warehouseImage = imageUrl || WarehouseDefaultImage;
  const workingScheduleString = getWorkingScheduleString(workingSchedule, t);

  const minBoxPrice = useGetMinBoxPriceAmount(props.warehouse);

  const discountedPrice = getBoxDiscountedPriceAmount({ warehouseDiscounts, boxPrice: minPrice });
  const discountedBoxPriceWithVat = getBoxPriceAmountWithVat(discountedPrice, props.warehouse, loggedUser?.accountType);
  const discountedBoxPrice = globalSettings?.showPriceWithVat ? discountedBoxPriceWithVat : discountedPrice;

  const handleSelectCard = useCallback((): void => {
    onSelectCard();
  }, [onSelectCard]);

  return (
    <div
      className={`bg-white rounded-lg border border-gray-200 overflow-hidden cursor-pointer transition-all duration-200 hover:border-accent ${
        isOutlined ? 'ring-2 ring-accent' : ''
      }`}
      onClick={handleSelectCard}
    >
      {/* Image on top, full width */}
      <div className="w-full h-[180px] overflow-hidden">
        <img className="w-full h-full object-cover" src={warehouseImage} alt={`${name} warehouse`} />
      </div>

      {/* Content */}
      <div className="p-4">
        <h3 className="text-2xl font-semibold font-tektur text-gray-800 mb-2">{name}</h3>

        <div className="flex items-start text-grey text-sm mb-1">
          <LocationIcon className="w-[18px] h-[18px] mr-2 text-grey flex-shrink-0" />
          <span className="flex-1">
            {fullAddress}
            {distance && <span className="ml-2 font-semibold text-sm">{t('{{distance}} km', { distance })}</span>}
          </span>
        </div>

        <div className="flex items-start text-grey text-sm mb-3">
          <TimeIcon className="w-[18px] h-[18px] mr-2 text-grey flex-shrink-0" />
          <span>{workingScheduleString}</span>
        </div>

        <div className="text-grey pt-2 mb-4">{t('{{availableBoxesAmount}} boxes free', { availableBoxesAmount })}</div>

        {Boolean(minBoxPrice) && (
          <div className="shrink-0 text-xl font-semibold font-tektur">
            {t('From {{discountedPrice}} {{currencySymbol}}/month', {
              currencySymbol,
              discountedPrice: formatPrice(discountedBoxPrice || minBoxPrice),
            })}
          </div>
        )}
      </div>
    </div>
  );
});
